import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Container,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  CardHeader,
  Divider,
  InputAdornment,
  IconButton,
  Tooltip,
  Chip,
} from '@mui/material';
import {
  School as SchoolIcon,
  Visibility,
  VisibilityOff,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import axios from 'axios';

// 定义登录页面的Props接口
interface LoginPageProps {
  onLogin: (username: string, password: string) => Promise<boolean>;
}

// 样式化组件
const LoginContainer = styled(Container)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '100vh',
  padding: theme.spacing(2),
  backgroundColor: theme.palette.background.default
}));

const LoginCard = styled(Card)(({ theme }) => ({
  width: '100%',
  maxWidth: 450,
  borderRadius: theme.shape.borderRadius,
  boxShadow: theme.shadows[3],
}));

const LogoBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  marginBottom: theme.spacing(2),
}));

const LoginButton = styled(Button)(({ theme }) => ({
  marginTop: theme.spacing(3),
  marginBottom: theme.spacing(2),
  padding: theme.spacing(1.2),
}));

const LoginPage: React.FC<LoginPageProps> = ({ onLogin }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [apiStatus, setApiStatus] = useState<string | null>(null);
  const [apiCheckLoading, setApiCheckLoading] = useState(false);
  const [apiCheckSuccess, setApiCheckSuccess] = useState<boolean | null>(null);

  // 处理登录表单提交
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // 表单验证
    if (!username.trim()) {
      setError('请输入用户名');
      return;
    }
    
    if (!password.trim()) {
      setError('请输入密码');
      return;
    }
    
    // 在登录前检查API连接状态
    if (apiCheckSuccess === false) {
      setError('无法连接到API服务器，请先检查API连接设置');
      return;
    }
    
    // 如果尚未进行API检查，先执行检查
    if (apiCheckSuccess === null) {
      try {
        setLoading(true);
        setError('正在验证API连接...');
        
        // 执行API状态检查
        await checkApiStatus();
        
        // 如果API检查失败，阻止登录
        if (apiCheckSuccess === false) {
          setError('API连接检查失败，无法登录。请检查API设置。');
          setLoading(false);
          return;
        }
      } catch (err) {
        setError('API连接检查失败，无法登录');
        setLoading(false);
        return;
      }
    }
    
    setLoading(true);
    setError('');
    
    try {
      const success = await onLogin(username, password);
      if (!success) {
        setError('用户名或密码错误');
      }
    } catch (err: any) {
      console.error('登录错误:', err);
      
      // 区分网络连接错误和其他错误类型
      if (err.message === 'Network Error' || 
          err.code === 'ECONNABORTED' || 
          (err.name === 'AxiosError' && !err.response)) {
        setError('无法连接到服务器，请检查网络连接或联系管理员');
        setApiCheckSuccess(false);
        setApiStatus('API状态: 离线或无法访问 - 连接失败');
      } else {
        setError('登录失败，请稍后重试');
      }
    } finally {
      setLoading(false);
    }
  };

  // 切换密码可见性
  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  // 重置API URL
  const handleResetApiUrl = () => {
    if (window.confirm('确定要重置API URL吗？这将清除保存的API地址。')) {
      // 提示用户输入新的API地址
      const newApiUrl = window.prompt(
        '请输入新的API服务器地址\n' +
        '例如: https://api.example.com 或 /api\n' +
        '留空并点击确定将清除当前地址，系统将在下次刷新页面时提示重新输入。'
      );
      
      // 用户点击取消
      if (newApiUrl === null) {
        return;
      }
      
      // 如果用户输入了新地址，进行验证和保存
      if (newApiUrl.trim() !== '') {
        // 验证URL是否有效
        const isValidUrl = (() => {
          // 如果是相对路径，则认为有效
          if (newApiUrl.startsWith('/')) return true;
          
          try {
            // 尝试解析URL
            const urlObj = new URL(newApiUrl);
            return !!urlObj.host; // 确保有主机名
          } catch (e) {
            console.error('URL解析错误:', e);
            return false;
          }
        })();
        
        if (!isValidUrl) {
          alert('您输入的API地址格式无效，请确保以"http://"或"https://"开头，或使用以"/"开头的相对路径。');
          return;
        }
        
        // 检查URL是否指向当前页面
        try {
          if (!newApiUrl.startsWith('/')) {
            const urlObj = new URL(newApiUrl);
            const currentUrlObj = new URL(window.location.href);
            
            if (urlObj.host === currentUrlObj.host && 
                urlObj.pathname === currentUrlObj.pathname) {
              alert('错误: API地址不能指向当前页面自身！这会导致API请求失败。');
              return;
            }
          }
        } catch (e) {
          console.error('URL比较错误:', e);
        }
        
        localStorage.setItem('api_url', newApiUrl.trim());
        alert('新的API地址已保存: ' + newApiUrl.trim());
      } else {
        // 用户留空，执行原来的重置操作
        if (typeof window.resetApiUrl === 'function') {
          window.resetApiUrl();
        } else {
          localStorage.removeItem('api_url');
        }
        alert('API URL已重置，请刷新页面');
      }
      
      window.location.reload();
    }
  };

  // 检查API状态
  const checkApiStatus = async () => {
    try {
      setApiCheckLoading(true);
      setApiStatus('正在检查API状态...');
      setApiCheckSuccess(null);
      
      // 添加随机参数避免缓存
      const timestamp = new Date().getTime();
      const url = `/api/auth/status?_=${timestamp}`;
      
      console.log(`正在检查API状态，URL: ${axios.defaults.baseURL ? axios.defaults.baseURL + url : url}`);
      
      const response = await axios.get(url);
      
      // 检查是否返回了有效的状态信息
      const status = response.data.status || '已连接';
      let message = response.data.message || '';
      
      // 将 "Cloudflare Workers API" 替换为 "云端"
      message = message.replace(/Cloudflare Workers API/g, "云端");
      
      // 如果两个字段都是undefined，则简化显示为"已连接"
      if (response.data.status === undefined && response.data.message === undefined) {
        setApiStatus('API状态: 已连接');
      } else {
        setApiStatus(`API状态: ${status} - ${message}`);
      }
      
      console.log('API状态检查成功:', response.data);
      setApiCheckSuccess(true);
      
      // 显示更详细的API信息
      if (response.data.userDataFile) {
        console.log('用户数据文件:', response.data.userDataFile);
        console.log('用户数据文件存在:', response.data.userDataExists);
        console.log('用户数量:', response.data.userCount);
      }
    } catch (error: any) {
      console.error('API状态检查失败:', error);
      setApiStatus(
        `API状态: 离线或无法访问 - ${error.message || '未知错误'}\n` +
        '请检查API URL配置，并确保服务器正在运行'
      );
      setApiCheckSuccess(false);
      
      // 记录详细错误信息
      if (error.response) {
        console.log('错误响应状态:', error.response.status);
        console.log('错误响应数据:', error.response.data);
      } else if (error.request) {
        console.log('请求发送但未收到响应 (可能是CORS或网络问题)');
      }
    } finally {
      setApiCheckLoading(false);
    }
  };

  // 组件挂载时自动检查API状态
  useEffect(() => {
    // 延迟1秒检查，以确保页面已完全加载
    const timer = setTimeout(() => {
      checkApiStatus();
    }, 1000);
    
    return () => clearTimeout(timer);
  }, []);

  // 添加额外的API地址验证
  useEffect(() => {
    // 检查API地址是否指向了前端自身
    const validateApiUrl = () => {
      const apiUrl = axios.defaults.baseURL || '';
      const currentUrl = window.location.href;
      
      try {
        // 如果是相对路径，假定有效
        if (apiUrl.startsWith('/')) return;
        
        if (apiUrl) {
          // 解析URL
          const apiUrlObj = new URL(apiUrl);
          const currentUrlObj = new URL(currentUrl);
          
          // 如果API URL与当前页面完全相同，显示警告
          if (apiUrlObj.host === currentUrlObj.host && 
              apiUrlObj.pathname === currentUrlObj.pathname) {
            setError('API地址错误：不能指向前端应用自身！请重置API地址。');
            setApiCheckSuccess(false);
            setApiStatus('API配置错误：API地址与前端应用相同，无法处理API请求');
          }
        }
      } catch (e) {
        console.error('URL验证错误:', e);
      }
    };
    
    validateApiUrl();
  }, []);

  return (
    <LoginContainer maxWidth={false}>
      <LoginCard>
        <CardHeader
          title={
            <Typography variant="h5" align="center" fontWeight="bold" color="primary">
              教育机构管理系统
            </Typography>
          }
        />
        <Divider />
        <CardContent>
          <LogoBox>
            <SchoolIcon color="primary" sx={{ fontSize: 60 }} />
          </LogoBox>
          
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          
          <form onSubmit={handleSubmit}>
            <TextField
              label="用户名"
              variant="outlined"
              fullWidth
              margin="normal"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              disabled={loading}
              autoFocus
            />
            
            <TextField
              label="密码"
              type={showPassword ? 'text' : 'password'}
              variant="outlined"
              fullWidth
              margin="normal"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={loading}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={handleTogglePasswordVisibility}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            
            <LoginButton
              type="submit"
              fullWidth
              variant="contained"
              color="primary"
              disabled={(loading) || (apiCheckSuccess === false)}
              startIcon={(loading) ? <CircularProgress size={20} color="inherit" /> : null}
            >
              {(loading) ? '登录中...' : '登录'}
            </LoginButton>
            
            {apiCheckSuccess === false && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                API服务器未响应，请先检查API连接设置
              </Alert>
            )}
          </form>
          
          <Typography variant="body2" color="textSecondary" align="center">
            © {new Date().getFullYear()} 教育机构管理系统
          </Typography>
          
          {/* 调试工具 */}
          <Box sx={{ mt: 2, pt: 2, borderTop: '1px dashed #ccc' }}>
            <Typography variant="caption" color="textSecondary" align="center" display="block">
              调试工具
            </Typography>
            
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
              <Tooltip title="设置或重置API服务器地址">
                <Button 
                  size="small" 
                  color="secondary" 
                  startIcon={<RefreshIcon />}
                  onClick={handleResetApiUrl}
                  sx={{ mr: 1 }}
                >
                  设置API地址
                </Button>
              </Tooltip>
              
              <Tooltip title="测试API服务器是否可访问">
                <Button 
                  size="small" 
                  color="info" 
                  onClick={checkApiStatus}
                  disabled={(apiCheckLoading)}
                  startIcon={(apiCheckLoading) ? <CircularProgress size={16} color="inherit" /> : null}
                >
                  {(apiCheckLoading) ? '检查中...' : '检查API状态'}
                </Button>
              </Tooltip>
            </Box>
            
            {apiStatus && (
              <Alert 
                severity={(apiCheckSuccess) ? "success" : "error"} 
                sx={{ mt: 1, fontSize: '0.75rem' }}
                icon={apiCheckSuccess ? <CheckCircleIcon /> : <ErrorIcon />}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', flexDirection: 'column', width: '100%' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                    {apiStatus}
                    {apiCheckSuccess && (
                      <Chip 
                        label="在线" 
                        color="success" 
                        size="small" 
                        sx={{ ml: 1, height: 20, fontSize: '0.7rem' }} 
                      />
                    )}
                  </Box>
                  {/* 隐藏API地址显示 */}
                  {/* <Typography 
                    variant="caption" 
                    color="textSecondary" 
                    align="left" 
                    display="block" 
                    sx={{ mt: 0.5, width: '100%', wordBreak: 'break-all' }}
                  >
                    当前API地址: {(axios.defaults.baseURL) || '(相对路径)'}
                  </Typography> */}
                </Box>
              </Alert>
            )}
            
            {!apiStatus && (
              /* 隐藏API地址显示 */
              <Typography 
                variant="caption" 
                color="textSecondary" 
                align="center" 
                display="block" 
                sx={{ mt: 1 }}
              >
                API状态未知
              </Typography>
            )}
          </Box>
        </CardContent>
      </LoginCard>
    </LoginContainer>
  );
};

export default LoginPage;

// 为window对象扩展resetApiUrl方法的类型定义
declare global {
  interface Window {
    resetApiUrl?: () => boolean;
  }
}